import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { useAppSelector, useAppDispatch } from "@/store";
import {
  fetchProjects,
  createProject,
  deleteProject,
} from "@/store/slices/projectSlice";
import { useNotification } from "@/context/NotificationContext";
import { getProjectStatus } from "@/utils/format";
import {
  PAGE_MAX_WIDTH,
  PROJECT_STATUS_COLORS,
  PROJECT_STATUS_DOT_COLORS,
  ProjectStatus,
} from "@/constants";
import { PageLoading } from "@/components/common/PageLoading";
import { PageHeader } from "@/components/common/PageHeader";
import { ManagementTabs } from "@/components/common/ManagementTabs";
import { EmptyTableRow } from "@/components/common/EmptyTableRow";
import { TableHeaderCell } from "@/components/common/TableHeaderCell";

export function ProjectsPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { list, loading } = useAppSelector((s) => s.projects);
  const { notify } = useNotification();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [startedAt, setStartedAt] = useState("");
  const [endedAt, setEndedAt] = useState("");

  useEffect(() => {
    dispatch(fetchProjects());
  }, [dispatch]);

  const handleCreate = async () => {
    await dispatch(
      createProject({
        name,
        description: description || undefined,
        startedAt: startedAt || undefined,
        endedAt: endedAt || undefined,
      }),
    ).unwrap();
    notify(t("common.success"), "success");
    setName("");
    setDescription("");
    setStartedAt("");
    setEndedAt("");
    setDialogOpen(false);
  };

  if (loading) return <PageLoading />;

  return (
    <Box sx={{ maxWidth: PAGE_MAX_WIDTH, mx: "auto" }}>
      <PageHeader
        title={t("projects.title")}
        subtitle={t("projects.subtitle")}
      />
      <ManagementTabs activeTab={0} />
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setDialogOpen(true)}
        >
          {t("projects.addProject")}
        </Button>
      </Box>
      <Card variant="outlined">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "action.hover" }}>
                <TableHeaderCell>{t("projects.projectName")}</TableHeaderCell>
                <TableHeaderCell>{t("projects.client")}</TableHeaderCell>
                <TableHeaderCell>{t("projects.startDate")}</TableHeaderCell>
                <TableHeaderCell>{t("projects.endDate")}</TableHeaderCell>
                <TableHeaderCell align="center">
                  {t("projects.status")}
                </TableHeaderCell>
                <TableHeaderCell align="right">
                  {t("common.actions")}
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((p: any) => {
                const status = getProjectStatus(p) as ProjectStatus;
                const clients = (p.clientProjects || [])
                  .map((cp: any) => cp.client?.name)
                  .filter(Boolean);
                return (
                  <TableRow key={p.id} hover>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: PROJECT_STATUS_DOT_COLORS[status],
                          }}
                        />
                        <Typography
                          sx={{
                            fontWeight: 600,
                          }}
                        >
                          {p.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        {clients.join(", ") || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {p.startedAt
                          ? new Date(p.startedAt).toLocaleDateString()
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {p.endedAt
                          ? new Date(p.endedAt).toLocaleDateString()
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={t(`projects.${status}`)}
                        size="small"
                        color={PROJECT_STATUS_COLORS[status]}
                        variant="outlined"
                        sx={{
                          fontWeight: 700,
                          fontSize: 10,
                          textTransform: "uppercase",
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small">
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => dispatch(deleteProject(p.id))}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {list.length === 0 && <EmptyTableRow colSpan={6} />}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("projects.addProject")}</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <TextField
            label={t("projects.projectName")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            margin="dense"
          />
          <TextField
            label={t("common.description")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            fullWidth
            margin="dense"
          />
          <TextField
            label={t("projects.startDate")}
            type="date"
            value={startedAt}
            onChange={(e) => setStartedAt(e.target.value)}
            fullWidth
            margin="dense"
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label={t("projects.endDate")}
            type="date"
            value={endedAt}
            onChange={(e) => setEndedAt(e.target.value)}
            fullWidth
            margin="dense"
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!name.trim()}
          >
            {t("common.create")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
